import {
  MOCK_MAM_KPIS,
  MOCK_MAM_MANAGERS,
  MOCK_MAM_GROUPS,
  MOCK_ALLOCATION_RULES,
  MOCK_CLIENT_ACCOUNTS,
  MOCK_MASTER_TRADES,
  MOCK_CHILD_TRADES,
  MOCK_PERFORMANCE_SERIES,
  MOCK_COMMISSION_RECORDS,
  MOCK_RISK_METRICS,
  MOCK_NOTIFICATIONS,
  MOCK_AUDIT_LOGS,
  MOCK_MAM_SETTINGS,
  MOCK_MAM_REPORTS,
} from '../mock';

import type {
  MAMManager,
  MAMGroup,
  AllocationRule,
  MAMClientAccount,
  MasterTrade,
  ChildTrade,
  MAMPerformancePoint,
  MAMCommissionRecord,
  MAMRiskMetrics,
  MAMReport,
  MAMNotification,
  MAMSettings,
  MAMAuditLog,
  MAMDashboardKPIs,
} from '../types';

const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

export const mamDashboardService = {
  async getKPIs(): Promise<MAMDashboardKPIs> {
    await delay();
    return MOCK_MAM_KPIS;
  },
};

export const mamManagerService = {
  async getAll(): Promise<MAMManager[]> {
    await delay();
    return MOCK_MAM_MANAGERS;
  },
  async getById(id: string): Promise<MAMManager | undefined> {
    await delay();
    return MOCK_MAM_MANAGERS.find(m => m.id === id);
  },
  async create(data: Partial<MAMManager>): Promise<MAMManager> {
    await delay(700);
    const newMgr: MAMManager = {
      id: `mgr-${Date.now()}`,
      name: data.name || 'New MAM Manager',
      email: data.email || 'manager@crm.com',
      accountNumber:
        data.accountNumber ||
        `MT5-${Math.floor(100000 + Math.random() * 900000)}`,
      status: 'pending',
      totalAUM: 0,
      activeClients: 0,
      groupsCount: 0,
      performanceFee: data.performanceFee || 20,
      managementFee: data.managementFee || 2.0,
      totalReturn: 0,
      monthlyReturn: 0,
      maxDrawdown: 0,
      winRate: 0,
      totalTrades: 0,
      riskLevel: data.riskLevel || 'medium',
      leverage: data.leverage || '1:200',
      tradingSymbols: data.tradingSymbols || ['EURUSD', 'GBPUSD'],
      joinedAt: new Date().toISOString(),
    };
    return newMgr;
  },
  async update(id: string, data: Partial<MAMManager>): Promise<MAMManager> {
    await delay(600);
    const existing = MOCK_MAM_MANAGERS.find(m => m.id === id);
    if (!existing) throw new Error('Manager not found');
    return { ...existing, ...data };
  },
};

export const mamGroupService = {
  async getAll(): Promise<MAMGroup[]> {
    await delay();
    return MOCK_MAM_GROUPS;
  },
  async getById(id: string): Promise<MAMGroup | undefined> {
    await delay();
    return MOCK_MAM_GROUPS.find(g => g.id === id);
  },
  async create(data: Partial<MAMGroup>): Promise<MAMGroup> {
    await delay(700);
    const newGrp: MAMGroup = {
      id: `grp-${Date.now()}`,
      name: data.name || 'New MAM Group',
      description: data.description || '',
      managerId: data.managerId || 'mgr-101',
      managerName:
        MOCK_MAM_MANAGERS.find(m => m.id === data.managerId)?.name ||
        'Apex Capital FX',
      allocationMethod: data.allocationMethod || 'balance_ratio',
      status: 'active',
      totalClients: 0,
      totalEquity: 0,
      totalBalance: 0,
      minDeposit: data.minDeposit || 1000,
      maxClients: data.maxClients || 50,
      leverage: data.leverage || '1:200',
      executionMode: data.executionMode || 'market',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    return newGrp;
  },
  async update(id: string, data: Partial<MAMGroup>): Promise<MAMGroup> {
    await delay(600);
    const existing = MOCK_MAM_GROUPS.find(g => g.id === id);
    if (!existing) throw new Error('Group not found');
    return { ...existing, ...data, updatedAt: new Date().toISOString() };
  },
  async delete(id: string): Promise<void> {
    await delay(500);
    void id;
  },
};

export const mamRuleService = {
  async getAll(): Promise<AllocationRule[]> {
    await delay();
    return MOCK_ALLOCATION_RULES;
  },
  async create(data: Partial<AllocationRule>): Promise<AllocationRule> {
    await delay(600);
    const newRule: AllocationRule = {
      id: `rule-${Date.now()}`,
      name: data.name || 'Custom Allocation Rule',
      type: data.type || 'fixed_lot',
      description: data.description || 'Custom allocation rule description',
      parameters: data.parameters || {},
      isDefault: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    return newRule;
  },
};

export const mamClientAccountService = {
  async getAll(): Promise<MAMClientAccount[]> {
    await delay();
    return MOCK_CLIENT_ACCOUNTS;
  },
  async getByGroup(groupId: string): Promise<MAMClientAccount[]> {
    await delay();
    return MOCK_CLIENT_ACCOUNTS.filter(c => c.groupId === groupId);
  },
  async disconnect(id: string): Promise<MAMClientAccount> {
    await delay(600);
    const account = MOCK_CLIENT_ACCOUNTS.find(c => c.id === id);
    if (!account) throw new Error('Account not found');
    return { ...account, status: 'disconnected' };
  },
};

export const mamTradeService = {
  async getMasterTrades(
    statusFilter?: 'open' | 'closed'
  ): Promise<MasterTrade[]> {
    await delay();
    if (statusFilter) {
      return MOCK_MASTER_TRADES.filter(t => t.status === statusFilter);
    }
    return MOCK_MASTER_TRADES;
  },
  async getChildTrades(masterTradeId?: string): Promise<ChildTrade[]> {
    await delay();
    if (masterTradeId) {
      return MOCK_CHILD_TRADES.filter(c => c.masterTradeId === masterTradeId);
    }
    return MOCK_CHILD_TRADES;
  },
  async retryFailedTrade(childTradeId: string): Promise<ChildTrade> {
    await delay(800);
    const child = MOCK_CHILD_TRADES.find(c => c.id === childTradeId);
    if (!child) throw new Error('Child trade not found');
    return {
      ...child,
      status: 'open',
      executionStatus: 'executed',
      errorMessage: undefined,
      retries: child.retries + 1,
    };
  },
};

export const mamPerformanceService = {
  async getSeries(): Promise<MAMPerformancePoint[]> {
    await delay();
    return MOCK_PERFORMANCE_SERIES;
  },
};

export const mamCommissionService = {
  async getAll(): Promise<MAMCommissionRecord[]> {
    await delay();
    return MOCK_COMMISSION_RECORDS;
  },
};

export const mamRiskService = {
  async getMetrics(): Promise<MAMRiskMetrics> {
    await delay();
    return MOCK_RISK_METRICS;
  },
  async updateMetrics(data: Partial<MAMRiskMetrics>): Promise<MAMRiskMetrics> {
    await delay(600);
    return { ...MOCK_RISK_METRICS, ...data };
  },
  async triggerEmergencyDisconnect(): Promise<void> {
    await delay(1000);
  },
};

export const mamReportService = {
  async getAll(): Promise<MAMReport[]> {
    await delay();
    return MOCK_MAM_REPORTS;
  },
  async generate(
    type: string,
    format: string,
    period: string
  ): Promise<MAMReport> {
    await delay(1000);
    return {
      id: `rpt-${Date.now()}`,
      title: `MAM ${type.toUpperCase()} Report - ${period}`,
      type: type as any,
      format: format as any,
      period,
      generatedAt: new Date().toISOString(),
      generatedBy: 'Current User',
      fileSize: '4.2 MB',
      status: 'ready',
    };
  },
};

export const mamNotificationService = {
  async getAll(): Promise<MAMNotification[]> {
    await delay();
    return MOCK_NOTIFICATIONS;
  },
  async markRead(id: string): Promise<void> {
    await delay(200);
    void id;
  },
};

export const mamSettingsService = {
  async get(): Promise<MAMSettings> {
    await delay();
    return MOCK_MAM_SETTINGS;
  },
  async update(data: Partial<MAMSettings>): Promise<MAMSettings> {
    await delay(700);
    return { ...MOCK_MAM_SETTINGS, ...data };
  },
};

export const mamAuditService = {
  async getAll(): Promise<MAMAuditLog[]> {
    await delay();
    return MOCK_AUDIT_LOGS;
  },
};
